import type { TelegramAuthChallenge, TelegramAuthResponse } from './telegram-auth';
export const LOCAL_SYNC_PROVIDERS = ['imessage', 'whatsapp', 'signal'] as const;
export type LocalSyncProvider = (typeof LOCAL_SYNC_PROVIDERS)[number];
export const AUTO_SYNC_PROVIDERS = [...LOCAL_SYNC_PROVIDERS, 'telegram'] as const;
export const TELEGRAM_AUTH_CHANNEL = 'connections:telegram-challenge';
export type AutoSyncProvider = (typeof AUTO_SYNC_PROVIDERS)[number];
export interface AppConnection {
  provider: AutoSyncProvider;
  enabled: boolean;
  status:
    | 'available'
    | 'connected'
    | 'syncing'
    | 'permission_required'
    | 'setup_required'
    | 'error';
  messageCount: number;
  deviceName?: string;
  accountLabel?: string;
  lastSync?: string;
  error?: string;
}
export interface AppConnectionsAPI {
  list(): Promise<AppConnection[]>;
  onChanged?(callback: () => void): () => void;
  connect(provider: AutoSyncProvider): Promise<AppConnection[]>;
  disconnect(provider: AutoSyncProvider): Promise<AppConnection[]>;
  openApp(provider: AutoSyncProvider): Promise<void>;
  openPermissions(): Promise<void>;
  onTelegramChallenge(callback: (challenge: TelegramAuthChallenge) => void): () => void;
  answerTelegramChallenge(response: TelegramAuthResponse): Promise<void>;
  cancelTelegramSetup(): Promise<void>;
  openTelegramConfirmation(link: string): Promise<void>;
}

export const MESSAGING_APPS: Record<AutoSyncProvider, { label: string; description: string }> = {
  imessage: { label: 'iMessage', description: 'Sync Messages from your Mac.' },
  whatsapp: { label: 'WhatsApp', description: 'Sync chats from WhatsApp for Mac.' },
  signal: { label: 'Signal', description: 'Sync conversations from Signal Desktop.' },
  telegram: { label: 'Telegram', description: 'Sign in to sync your Telegram conversations.' },
};

export type ProviderConnectionState = Pick<
  AppConnection,
  'provider' | 'enabled' | 'status' | 'lastSync' | 'error'
>;

/**
 * Source failures arrive as "App: message" parts joined with "; ". Returns the parts no app card
 * already shows, such as an upload failure, so the same problem is never reported twice.
 */
export function unexplainedConnectionError(
  error: string,
  shownOnCards: readonly string[]
): string | undefined {
  const remaining = error
    .split('; ')
    .filter((part) => part.trim() && !shownOnCards.some((shown) => part.endsWith(shown)));
  return remaining.length ? remaining.join('; ') : undefined;
}
